import { useState } from 'react'
import _ from 'lodash'
import { httpHandler, safeAwait } from '@vzmi/component_ec_nodejs_util/dist/lib/FetchDataUtil'
import { useQueryString } from '@vzmi/shp_shared_compo/dist/hook/useQueryString.js'
import { COMMA } from '@vzmi/component_ec_nodejs_util/dist/lib/parserUtil'
import useOnInit from '../hook/useOnInit'
import errorHelper from '../../../../lib/errorHelper'
import fetchBatchProposals from '../request/fetchBatchProposals'
import fetchBatchListings from '../request/fetchBatchListings'

const OVERVIEW_TYPE = {
  EXECUTE: 'execute',
  REVERT: 'revert'
}

const OVERVIEW_TYPE_MAPPING_ID = {
  [OVERVIEW_TYPE.EXECUTE]: 'executeProposalId',
  [OVERVIEW_TYPE.REVERT]: 'revertProposalId'
}

const useBatchProposalById = (
  fetchBatchProposal,
  fetchMultipleProposals,
  fetchListings,
  options = {}
) => {
  const [batchProposal, setBatchProposal] = useState({})
  const [proposals, setProposals] = useState([])
  const [listings, setListings] = useState([])
  const [isBatchProposalLoading, setIsBatchProposalLoading] = useState(false)
  const [isDependencyRequestLoading, setIsDependencyRequestLoading] = useState(false)
  const [error, setError] = useState(null)
  const proposalId = useQueryString(global.window, 'id', false)
  const overviewType = useQueryString(global.window, 'type', false)
  const {
    onError = () => {},
    onSuccess = () => {}
  } = options
  const isValidOverviewType = overviewType in OVERVIEW_TYPE_MAPPING_ID
  const fetchBatchProposalParams = {
    fields: '+details',
    id: proposalId
  }

  const handleError = e => {
    const errorMessage = errorHelper.parseErrorMessage(e)
    setError(errorMessage)
    setIsBatchProposalLoading(false)
    setIsDependencyRequestLoading(false)
    onError(errorMessage)
  }

  const handleSuccess = result => {
    const {
      proposals: newProposals,
      listings: newListings,
      isBatchProposalLoading: newIsBatchProposalLoading,
      isDependencyRequestLoading: newIsDependencyRequestLoading
    } = result
    setProposals(newProposals)
    setListings(newListings)
    setIsBatchProposalLoading(newIsBatchProposalLoading)
    setIsDependencyRequestLoading(newIsDependencyRequestLoading)
    onSuccess(result)
  }

  const trigger = async () => {
    if (_.isEmpty(proposalId)) {
      onError('沒有批次提案編號')
      return
    }

    if (!isValidOverviewType) {
      onError('批次提案檢視類型錯誤')
      return
    }

    setIsBatchProposalLoading(true)
    setIsDependencyRequestLoading(true)
    const [fetchBatchProposalError, newBatchProposal] = await safeAwait(
      fetchBatchProposal(fetchBatchProposalParams).then(httpHandler)
    )

    if (fetchBatchProposalError) {
      handleError(fetchBatchProposalError)
      return
    }

    setBatchProposal(newBatchProposal)
    setIsBatchProposalLoading(false)

    const batchDetails = _.get(newBatchProposal, 'details', [])
    const fetchMultipleProposalsParams = _.flow(
      () => _.map(batchDetails, OVERVIEW_TYPE_MAPPING_ID[overviewType]),
      ids => ({ id: _.join(ids, COMMA) })
    )()
    const fetchListingsParams = _.flow(
      () => _.map(batchDetails, 'listingId'),
      ids => ({ id: _.join(ids, COMMA) })
    )()
    const results = await Promise.all([
      _.isEmpty(fetchMultipleProposalsParams.id)
        ? Promise.resolve([])
        : fetchBatchProposals({ fetchMultipleProposals }, fetchMultipleProposalsParams),
      _.isEmpty(fetchListingsParams.id)
        ? Promise.resolve([])
        : fetchBatchListings({ fetchListings }, fetchListingsParams)
    ])
    const [
      [fetchBatchProposalsError, newProposals],
      [fetchListingsError, newListings]
    ] = results
    const dependencyError = fetchBatchProposalsError || fetchListingsError
    if (dependencyError) {
      handleError(dependencyError)
      return
    }

    handleSuccess({
      batchProposal: newBatchProposal,
      proposals: newProposals,
      listings: newListings,
      isBatchProposalLoading: false,
      isDependencyRequestLoading: false,
      error: null,
      trigger
    })
  }

  useOnInit(() => trigger())

  return {
    batchProposal,
    proposals,
    listings,
    isBatchProposalLoading,
    isDependencyRequestLoading,
    error,
    trigger
  }
}

export default useBatchProposalById
